// client/src/routes/app/route.tsx — the /app layout · UI-003 (04 §6.1, §9.2, §9.2.2), guarded
// by AUTH-004.
//
// 04 §6.1 calls this „the `_app.tsx` shell layout"; with the `/app` base path its file-based
// name is `app/route.tsx` — the same component, as §9.2's listing notes in place. It renders
// the shell ONCE around every `/app/*` screen, which is what makes the sidebar's scroll
// position and the drawer's state survive navigation.
//
// The guard is §9.2.2's, as UI-003 left it written out in a comment for AUTH-004 to uncomment,
// with two differences the document could not have known:
//   • it AWAITS `ensureSession()` rather than reading `getSession()`: on a hard refresh the
//     store is empty until the refresh cookie has been tried once, and a guard that read null
//     before then would bounce every reload to the login page;
//   • the tenant redirect went by `href`, because `/portal` was the PORTAL items' route and did
//     not exist yet — a typed `to` would not compile, and a tenant who signed in here reached
//     SCR-SYS-404 instead of a portal (DEVIATIONS D-184). PORTAL-002 phase 1 built the shell,
//     so the redirect is typed and the route tree now checks it. D-184 is closed.

import { Link, Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { toProblemDetails } from '@/api/problem-details';
import { AppShell } from '@/components/layout/AppShell';
import { SystemState } from '@/components/ui/states';
import { PLATFORM_LANDING } from '@/features/auth/redirect';
import { ensureSession } from '@/session';

export const Route = createFileRoute('/app')({
  beforeLoad: async ({ location }) => {
    const session = await ensureSession();
    if (!session) throw redirect({ to: '/login', search: { redirect: location.href } });
    // Still the ROLE, and deliberately not the lease count the portal guard now admits on
    // (D-1727). The two questions are different: „may this account read a portal" is about a
    // lease it is a party to, and „does this account belong in the owner console" is about the
    // organization its token names. A landlord who rents a flat elsewhere holds `owner` here
    // and stays — that is the whole point of the item; a `tenant`-role account has nothing to
    // read under this shell whether or not it rents anything.
    if (session.role === 'tenant') throw redirect({ to: '/portal' });
    // The mirror of the tenant bounce above, for the other role with no business here: a
    // platform admin holds no organization permission, so every screen under this shell would
    // render SCR-SYS-403. „Acces interzis" twenty-two times is not an answer — the platform
    // area is (D-1642).
    if (session.permissions.includes('Platform.Admin')) throw redirect({ href: PLATFORM_LANDING });

    // A delegated account that has not said whose portfolio it is opening is asked, on a page
    // of its own, before any shell renders (D-1707). Without this the first thing a manager
    // met after signing in was an empty portfolio explaining that they had to open a dropdown
    // — a state the product knows how to resolve, presented as a puzzle.
    //
    // Last, and after the two role bounces above: a tenant and a platform admin have no
    // business here whether or not they hold clients, and this question is only the one
    // remaining reason an `/app` visitor cannot be shown `/app`.
    if (session.clients.length > 0 && session.activeClientId === null) {
      throw redirect({ to: '/select-client', search: { redirect: location.href } });
    }
  },
  component: AppLayout,
  // §8.20: „All three render inside the active shell (never a blank page)". Declared HERE
  // rather than only on the root because a child route's failure is caught by the nearest
  // ancestor boundary and rendered at the child's position — which, for anything under
  // `/app`, is this route's `<Outlet />`, with the header and sidebar still standing. The
  // root's copies (routes/__root.tsx) keep serving the URLs that never got this far.
  errorComponent: AppErrorComponent,
  notFoundComponent: AppNotFoundComponent,
});

function AppLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/** SCR-SYS-500, inside the shell. `reset` is the router's retry — §5.11 wants one offered. */
function AppErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();
  const problem = toProblemDetails(error);
  return (
    <SystemState
      icon="exclamation-triangle"
      title={t('errors.title')}
      body={problem.detail ?? t('errors.generic')}
      correlationId={problem.correlationId}
      actions={
        <button type="button" className="btn btn-primary" onClick={reset}>
          {t('common.retry')}
        </button>
      }
    />
  );
}

/** SCR-SYS-404, inside the shell — the navigation the user needs stays on screen. */
function AppNotFoundComponent() {
  const { t } = useTranslation();
  return (
    <SystemState
      icon="question-mark-circle"
      title={t('errors.notFoundTitle')}
      body={t('errors.notFoundBody')}
      actions={
        <Link to="/app/dashboard" className="btn btn-primary">
          {t('nav.dashboard')}
        </Link>
      }
    />
  );
}
